import React, { useEffect, useRef, useState } from "react";
import ActionButton from "../components/ActionButton.tsx";
import { supabase } from "../SupabaseClient.tsx";
import { useNavigate, useLocation } from "react-router-dom";
import { PiPlus, PiTrash, PiSpinnerGap, PiPencil, PiCheck, PiX, PiStar } from "react-icons/pi";
import "./Settings.css";
import { clearAllFilesFromStorage } from "../utils/uploadStorage.ts";
import { getBillingPortalUrl } from "../utils/checkout.ts";

// Interface for authorized email
interface AuthorizedEmail {
  id: string;
  email: string;
  created_at: string;
}

const Settings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [interestRate, setInterestRate] = useState<number | null>(null);
  const [, setRetirementDate] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isEmailProvider, setIsEmailProvider] = useState<boolean>(false);
  const [socialProvider, setSocialProvider] = useState<string | null>(null);
  // New state for subscription popup
  const [showSubscriptionPopup, setShowSubscriptionPopup] =
    useState<boolean>(false);
  // Add state for userId
  const [userId, setUserId] = useState<string | null>(null);
  // State for authorized emails
  const [authorizedEmails, setAuthorizedEmails] = useState<AuthorizedEmail[]>(
    []
  );
  const [newEmail, setNewEmail] = useState<string>("");
  const [isAddingEmail, setIsAddingEmail] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [deletingEmailId, setDeletingEmailId] = useState<string | null>(null);
  const [editingEmailId, setEditingEmailId] = useState<string | null>(null);
  const [editingEmailValue, setEditingEmailValue] = useState<string>("");
  const [originalEmailValue, setOriginalEmailValue] = useState<string>("");
  const [isSavingEmail, setIsSavingEmail] = useState<boolean>(false);
  // State for beneficiaries
  const [beneficiaries, setBeneficiaries] = useState<string[]>([]);
  const [newBeneficiary, setNewBeneficiary] = useState<string>("");
  const [isAddingBeneficiary, setIsAddingBeneficiary] = useState<boolean>(false);
  const [beneficiaryError, setBeneficiaryError] = useState<string | null>(null);
  const [deletingBeneficiaryIndex, setDeletingBeneficiaryIndex] = useState<number | null>(null);
  const [editingBeneficiaryIndex, setEditingBeneficiaryIndex] = useState<number | null>(null);
  const [editingBeneficiaryValue, setEditingBeneficiaryValue] = useState<string>("");
  const [originalBeneficiaryValue, setOriginalBeneficiaryValue] = useState<string>("");
  const [isSavingBeneficiary, setIsSavingBeneficiary] = useState<boolean>(false);
  const [beneficiaryMessage, setBeneficiaryMessage] = useState<string | null>(null);
  const [settingDefaultIndex, setSettingDefaultIndex] = useState<number | null>(null);

  // Fetch the interest rate from the database
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const { data: authData } = await supabase.auth.getUser();
        if (authData && authData.user) {
          setUserEmail(authData.user.email ?? null);
          // Store user ID for later use in updates
          setUserId(authData.user.id);

          // Check if the user authenticated with email provider
          const provider = authData.user.app_metadata?.provider;
          setIsEmailProvider(provider === "email" || !provider);

          // Store the social provider name if not email
          if (provider && provider !== "email") {
            setSocialProvider(provider);
          }

          // Fetch authorized emails
          const { data: emailsData, error: emailsError } = await supabase
            .from("upload_authorized_emails")
            .select("id, email, created_at")
            .order("created_at", { ascending: true });

          if (emailsError) {
            console.error("Error fetching authorized emails:", emailsError);
          } else {
            setAuthorizedEmails(emailsData || []);
          }
        }

        const { data, error } = await supabase
          .from("users")
          .select("interest_rate, retirement_date, beneficiaries")
          .single(); // Assuming the current user is already authenticated

        if (error) {
          console.error("Error fetching interest rate:", error);
        } else {
          setInterestRate(data.interest_rate);
          setRetirementDate(data.retirement_date);
          setBeneficiaries(data.beneficiaries || ["Myself"]);
        }
      } catch (error) {
        console.error("Unexpected error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []);

  // Scroll to a section once data has loaded when linked to directly (e.g. /settings#beneficiaries)
  const hasScrolledToHash = useRef(false);
  useEffect(() => {
    if (location.hash && beneficiaries.length > 0 && !hasScrolledToHash.current) {
      hasScrolledToHash.current = true;
      document
        .getElementById(location.hash.slice(1))
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash, beneficiaries.length]);

  const handleLogout = async () => {
    try {
      // Clear any pending uploads from IndexedDB before logout
      await clearAllFilesFromStorage();
      await supabase.auth.signOut();
      navigate("/receipt-library"); // Redirect to the landing page after logout
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const handleManageSubscription = () => {
    // Show confirmation popup instead of navigating immediately
    setShowSubscriptionPopup(true);
  };

  const handleUpdatePassword = () => {
    navigate("/update-password");
  };

  const confirmSubscriptionNavigation = () => {
    window.open(getBillingPortalUrl(userEmail), "_blank");
    setShowSubscriptionPopup(false);
  };

  const cancelSubscriptionNavigation = () => {
    setShowSubscriptionPopup(false);
  };

  function handleInterestRateChange(
    event: React.ChangeEvent<HTMLInputElement>
  ): void {
    const value = event.target.value;
    // Allow empty input to clear the field
    if (value === "") {
      setInterestRate(null);
      // Update database with null value if user has cleared the field
      if (userId) {
        updateInterestRate(null);
      }
      return;
    }
    const num = parseFloat(value);
    if (!isNaN(num)) {
      setInterestRate(num);
      // Update the database with the new value
      if (userId) {
        updateInterestRate(num);
      }
    }
  }

  // Extract update logic to a separate function
  const updateInterestRate = async (rate: number | null) => {
    try {
      const { error } = await supabase
        .from("users")
        .update({ interest_rate: rate })
        .eq("user_id", userId);

      if (error) {
        console.error("Error updating interest rate:", error);
      }
    } catch (err) {
      console.error("Unexpected error updating interest rate:", err);
    }
  };

  // Validate email format
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Add a new authorized email
  const handleAddEmail = async () => {
    setEmailError(null);

    // Validate email
    if (!newEmail.trim()) {
      setEmailError("Please enter an email address");
      return;
    }

    if (!isValidEmail(newEmail.trim())) {
      setEmailError("Please enter a valid email address");
      return;
    }

    // Check if email is already in the list
    if (
      authorizedEmails.some(
        (e) => e.email.toLowerCase() === newEmail.trim().toLowerCase()
      )
    ) {
      setEmailError("This email is already authorized");
      return;
    }

    // Check if email is the user's primary email
    if (
      userEmail &&
      newEmail.trim().toLowerCase() === userEmail.toLowerCase()
    ) {
      setEmailError("This is already your primary account email");
      return;
    }

    setIsAddingEmail(true);

    try {
      const { data, error } = await supabase
        .from("upload_authorized_emails")
        .insert({
          user_id: userId,
          email: newEmail.trim().toLowerCase(),
        })
        .select("id, email, created_at")
        .single();

      if (error) {
        if (error.code === "23505") {
          // Unique constraint violation
          setEmailError("This email is already linked to another account");
        } else {
          setEmailError("Failed to add email. Please try again.");
          console.error("Error adding email:", error);
        }
      } else if (data) {
        setAuthorizedEmails([...authorizedEmails, data]);
        setNewEmail("");
      }
    } catch (err) {
      setEmailError("An unexpected error occurred");
      console.error("Unexpected error adding email:", err);
    } finally {
      setIsAddingEmail(false);
    }
  };

  // Remove an authorized email
  const handleRemoveEmail = async (emailId: string) => {
    setDeletingEmailId(emailId);

    try {
      const { error } = await supabase
        .from("upload_authorized_emails")
        .delete()
        .eq("id", emailId);

      if (error) {
        console.error("Error removing email:", error);
      } else {
        setAuthorizedEmails(authorizedEmails.filter((e) => e.id !== emailId));
      }
    } catch (err) {
      console.error("Unexpected error removing email:", err);
    } finally {
      setDeletingEmailId(null);
    }
  };

  // Handle Enter key press in email input
  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !isAddingEmail) {
      handleAddEmail();
    }
  };

  // Start editing an authorized email
  const handleStartEditEmail = (emailId: string, currentEmail: string) => {
    setEditingEmailId(emailId);
    setEditingEmailValue(currentEmail);
    setOriginalEmailValue(currentEmail);
    setEmailError(null);
  };

  // Cancel editing an authorized email
  const handleCancelEditEmail = () => {
    setEditingEmailId(null);
    setEditingEmailValue("");
    setOriginalEmailValue("");
    setEmailError(null);
  };

  // Save edited email
  const handleSaveEditEmail = async () => {
    if (editingEmailId === null) return;

    setEmailError(null);

    // Validate email
    if (!editingEmailValue.trim()) {
      setEmailError("Please enter an email address");
      return;
    }

    if (!isValidEmail(editingEmailValue.trim())) {
      setEmailError("Please enter a valid email address");
      return;
    }

    // Check if email is already in the list (excluding the one being edited)
    if (
      authorizedEmails.some(
        (e) => e.id !== editingEmailId && e.email.toLowerCase() === editingEmailValue.trim().toLowerCase()
      )
    ) {
      setEmailError("This email is already authorized");
      return;
    }

    // Check if email is the user's primary email
    if (
      userEmail &&
      editingEmailValue.trim().toLowerCase() === userEmail.toLowerCase()
    ) {
      setEmailError("This is already your primary account email");
      return;
    }

    setIsSavingEmail(true);

    try {
      const { data, error } = await supabase
        .from("upload_authorized_emails")
        .update({ email: editingEmailValue.trim().toLowerCase() })
        .eq("id", editingEmailId)
        .select("id, email, created_at")
        .single();

      if (error) {
        if (error.code === "23505") {
          setEmailError("This email is already linked to another account");
        } else {
          setEmailError("Failed to update email. Please try again.");
          console.error("Error updating email:", error);
        }
      } else if (data) {
        setAuthorizedEmails(
          authorizedEmails.map((e) => (e.id === editingEmailId ? data : e))
        );
        setEditingEmailId(null);
        setEditingEmailValue("");
        setOriginalEmailValue("");
      }
    } catch (err) {
      setEmailError("An unexpected error occurred");
      console.error("Unexpected error updating email:", err);
    } finally {
      setIsSavingEmail(false);
    }
  };

  // Handle Enter/Escape key press in edit email input
  const handleEditEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !isSavingEmail) {
      handleSaveEditEmail();
    } else if (e.key === "Escape") {
      handleCancelEditEmail();
    }
  };

  // Add a new beneficiary
  const handleAddBeneficiary = async () => {
    setBeneficiaryError(null);
    setBeneficiaryMessage(null);

    // Validate beneficiary name
    if (!newBeneficiary.trim()) {
      setBeneficiaryError("Please enter a beneficiary name");
      return;
    }

    // Check if beneficiary is already in the list
    if (
      beneficiaries.some(
        (b) => b.toLowerCase() === newBeneficiary.trim().toLowerCase()
      )
    ) {
      setBeneficiaryError("This beneficiary is already in your list");
      return;
    }

    setIsAddingBeneficiary(true);

    try {
      const updatedBeneficiaries = [...beneficiaries, newBeneficiary.trim()];
      const { error } = await supabase
        .from("users")
        .update({ beneficiaries: updatedBeneficiaries })
        .eq("user_id", userId);

      if (error) {
        setBeneficiaryError("Failed to add beneficiary. Please try again.");
        console.error("Error adding beneficiary:", error);
      } else {
        setBeneficiaries(updatedBeneficiaries);
        setNewBeneficiary("");
      }
    } catch (err) {
      setBeneficiaryError("An unexpected error occurred");
      console.error("Unexpected error adding beneficiary:", err);
    } finally {
      setIsAddingBeneficiary(false);
    }
  };

  // Remove a beneficiary
  const handleRemoveBeneficiary = async (index: number) => {
    // Prevent deletion if it's the last beneficiary
    if (beneficiaries.length <= 1) {
      setBeneficiaryError("You must have at least one beneficiary.");
      return;
    }

    setDeletingBeneficiaryIndex(index);

    try {
      const updatedBeneficiaries = beneficiaries.filter((_, i) => i !== index);
      const { error } = await supabase
        .from("users")
        .update({ beneficiaries: updatedBeneficiaries })
        .eq("user_id", userId);

      if (error) {
        console.error("Error removing beneficiary:", error);
      } else {
        setBeneficiaries(updatedBeneficiaries);
      }
    } catch (err) {
      console.error("Unexpected error removing beneficiary:", err);
    } finally {
      setDeletingBeneficiaryIndex(null);
    }
  };

  // Handle Enter key press in beneficiary input
  const handleBeneficiaryKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !isAddingBeneficiary) {
      handleAddBeneficiary();
    }
  };

  // Start editing a beneficiary
  const handleStartEditBeneficiary = (index: number) => {
    setEditingBeneficiaryIndex(index);
    setEditingBeneficiaryValue(beneficiaries[index]);
    setOriginalBeneficiaryValue(beneficiaries[index]);
    setBeneficiaryError(null);
    setBeneficiaryMessage(null);
  };

  // Cancel editing a beneficiary
  const handleCancelEditBeneficiary = () => {
    setEditingBeneficiaryIndex(null);
    setEditingBeneficiaryValue("");
    setOriginalBeneficiaryValue("");
    setBeneficiaryError(null);
  };

  // Save edited beneficiary
  const handleSaveEditBeneficiary = async () => {
    if (editingBeneficiaryIndex === null) return;

    setBeneficiaryError(null);

    // Validate beneficiary name
    if (!editingBeneficiaryValue.trim()) {
      setBeneficiaryError("Please enter a beneficiary name");
      return;
    }

    // Check if beneficiary is already in the list (excluding the one being edited)
    if (
      beneficiaries.some(
        (b, idx) => idx !== editingBeneficiaryIndex && b.toLowerCase() === editingBeneficiaryValue.trim().toLowerCase()
      )
    ) {
      setBeneficiaryError("This beneficiary is already in your list");
      return;
    }

    setIsSavingBeneficiary(true);
    setBeneficiaryMessage(null);

    try {
      const oldName = beneficiaries[editingBeneficiaryIndex];
      const newName = editingBeneficiaryValue.trim();
      const updatedBeneficiaries = [...beneficiaries];
      updatedBeneficiaries[editingBeneficiaryIndex] = newName;

      const { error } = await supabase
        .from("users")
        .update({ beneficiaries: updatedBeneficiaries })
        .eq("user_id", userId);

      if (error) {
        setBeneficiaryError("Failed to update beneficiary. Please try again.");
        console.error("Error updating beneficiary:", error);
      } else {
        setBeneficiaries(updatedBeneficiaries);
        setEditingBeneficiaryIndex(null);
        setEditingBeneficiaryValue("");
        setOriginalBeneficiaryValue("");

        // Carry the rename over to existing receipts
        const { data: renamedOrders, error: ordersError } = await supabase
          .from("orders")
          .update({ beneficiary: newName })
          .eq("user_id", userId)
          .eq("beneficiary", oldName)
          .select("id");

        if (ordersError) {
          setBeneficiaryError(
            "Beneficiary renamed, but existing receipts could not be updated."
          );
          console.error("Error renaming beneficiary on receipts:", ordersError);
        } else if (renamedOrders && renamedOrders.length > 0) {
          setBeneficiaryMessage(
            `Updated ${renamedOrders.length} existing receipt${renamedOrders.length === 1 ? "" : "s"} from "${oldName}" to "${newName}".`
          );
        }
      }
    } catch (err) {
      setBeneficiaryError("An unexpected error occurred");
      console.error("Unexpected error updating beneficiary:", err);
    } finally {
      setIsSavingBeneficiary(false);
    }
  };

  // Move a beneficiary to the top of the list so it's used as the default
  const handleSetDefaultBeneficiary = async (index: number) => {
    if (index === 0) return;

    setBeneficiaryError(null);
    setBeneficiaryMessage(null);
    setSettingDefaultIndex(index);

    try {
      const updatedBeneficiaries = [
        beneficiaries[index],
        ...beneficiaries.filter((_, i) => i !== index),
      ];
      const { error } = await supabase
        .from("users")
        .update({ beneficiaries: updatedBeneficiaries })
        .eq("user_id", userId);

      if (error) {
        setBeneficiaryError("Failed to update default beneficiary. Please try again.");
        console.error("Error setting default beneficiary:", error);
      } else {
        setBeneficiaries(updatedBeneficiaries);
      }
    } catch (err) {
      setBeneficiaryError("An unexpected error occurred");
      console.error("Unexpected error setting default beneficiary:", err);
    } finally {
      setSettingDefaultIndex(null);
    }
  };

  // Handle Enter/Escape key press in edit input
  const handleEditBeneficiaryKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !isSavingBeneficiary) {
      handleSaveEditBeneficiary();
    } else if (e.key === "Escape") {
      handleCancelEditBeneficiary();
    }
  };

  return (
    <div className="global-page-screen">
      <div className="page">
        <div className="page-header">
          <div className="page-header-title">
            <h1>Settings</h1>
            <p>Update your account settings.</p>
          </div>
          <ActionButton
            text="Logout"
            onClick={handleLogout}
            responsive={false}
            secondary={true}
          />
        </div>
        <div className="settings-label">
          <div className="container-label-header">
            <h2>Compound Growth Rate</h2>
            <p>
              Set the assumed compound growth rate (CAGR) for your HSA account.
              This will be used to calculate historical returns from delayed
              reimbursement and forecast future growth.
            </p>
          </div>
          <div className="settings-label-input">
            <input
              type="number"
              value={interestRate !== null ? interestRate : ""}
              onChange={handleInterestRateChange}
            />
            <h2>%</h2>
          </div>
        </div>
        {/* <div className="settings-label">
          <div className="container-label-header">
            <h2>Expected Retirement Date</h2>
            <p>Set your expected retirement date.</p>
          </div>
          <div className="settings-label-input">
            <input
              type="number"
              value={retirementDate ? retirementDate.split("-")[0] : ""}
              onChange={handleRetirementDateChange}
              placeholder="2050"
              min="2000"
              max="2100"
            />
          </div>
        </div> */}
        <div className="settings-label">
          <div className="container-label-header">
            <h2>Subscription</h2>
            <p>Manage your Shoebox Plus subscription.</p>
          </div>
          <ActionButton
            text="Manage"
            onClick={handleManageSubscription}
            secondary={true}
          />
        </div>

        {/* Password section with link to dedicated password update page */}
        <div className="settings-label">
          <div className="container-label-header">
            <h2>Security</h2>
            {isEmailProvider ? (
              <p>Update your account password.</p>
            ) : (
              <p className="social-login-message">
                Password management is disabled because you are signed in with
                {socialProvider ? ` ${socialProvider}` : " a social login"}.
              </p>
            )}
          </div>
          <ActionButton
            text="Change Password"
            onClick={handleUpdatePassword}
            secondary={true}
            disabled={!isEmailProvider}
          />
        </div>

        {/* Authorized Email Addresses Section */}
        <div className="settings-label settings-label-vertical">
          <div className="container-label-header">
            <h2>Email Upload Addresses</h2>
            <p>
              Add additional email addresses that can upload receipts to your
              account by sending them to <strong>upload@shoebox.io</strong>.
            </p>
          </div>

          {/* Primary email display */}
          <div className="authorized-emails-list">
            <div className="authorized-email-item primary-email">
              <span className="email-address">{userEmail}</span>
              <span className="email-badge">Primary</span>
            </div>

            {/* List of additional authorized emails */}
            {authorizedEmails.map((emailItem) => (
              <div key={emailItem.id} className="authorized-email-item">
                {editingEmailId === emailItem.id ? (
                  <>
                    <input
                      type="email"
                      value={editingEmailValue}
                      onChange={(e) => {
                        setEditingEmailValue(e.target.value);
                        setEmailError(null);
                      }}
                      onKeyDown={handleEditEmailKeyDown}
                      className="add-email-input"
                      autoFocus
                    />
                    <div className="beneficiary-edit-actions">
                      <button
                        className="remove-email-button"
                        onClick={handleSaveEditEmail}
                        disabled={isSavingEmail || editingEmailValue.trim().toLowerCase() === originalEmailValue.trim().toLowerCase() || !editingEmailValue.trim()}
                        title="Save"
                      >
                        {isSavingEmail ? (
                          <PiSpinnerGap className="spin-animation" />
                        ) : (
                          <PiCheck />
                        )}
                      </button>
                      <button
                        className="remove-email-button"
                        onClick={handleCancelEditEmail}
                        disabled={isSavingEmail}
                        title="Cancel"
                      >
                        <PiX />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="email-address">{emailItem.email}</span>
                    <div className="beneficiary-edit-actions">
                      <button
                        className="remove-email-button"
                        onClick={() => handleStartEditEmail(emailItem.id, emailItem.email)}
                        title="Edit email"
                      >
                        <PiPencil />
                      </button>
                      <button
                        className="remove-email-button"
                        onClick={() => handleRemoveEmail(emailItem.id)}
                        disabled={deletingEmailId === emailItem.id}
                        title="Remove email"
                      >
                        {deletingEmailId === emailItem.id ? (
                          <PiSpinnerGap className="spin-animation" />
                        ) : (
                          <PiTrash />
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Add new email input */}
          <div className="add-email-container">
            <input
              type="email"
              placeholder="Add another email address"
              value={newEmail}
              onChange={(e) => {
                setNewEmail(e.target.value);
                setEmailError(null);
              }}
              onKeyDown={handleEmailKeyDown}
              className={`add-email-input ${emailError ? "input-error" : ""}`}
            />
            <ActionButton
              icon={
                isAddingEmail ? (
                  <PiSpinnerGap className="spin-animation" />
                ) : (
                  <PiPlus />
                )
              }
              text="Add"
              onClick={handleAddEmail}
              secondary={true}
              disabled={isAddingEmail}
              responsive={true}
            />
          </div>
          {emailError && <p className="email-error-message">{emailError}</p>}
        </div>

        {/* Beneficiaries Section */}
        <div id="beneficiaries" className="settings-label settings-label-vertical">
          <div className="container-label-header">
            <h2>Beneficiaries</h2>
            <p>
              Manage the list of beneficiaries for your HSA account. When you
              upload a receipt, Shoebox matches the name on it to this list and
              falls back to your default beneficiary. Click the star to change
              the default. Renaming a beneficiary also updates your existing
              receipts.
            </p>
          </div>

          {/* List of beneficiaries */}
          <div className="authorized-emails-list">
            {beneficiaries.map((beneficiary, index) => (
              <div key={index} className={`authorized-email-item ${index === 0 ? 'primary-email' : ''}`}>
                {editingBeneficiaryIndex === index ? (
                  <>
                    <input
                      type="text"
                      value={editingBeneficiaryValue}
                      onChange={(e) => {
                        setEditingBeneficiaryValue(e.target.value);
                        setBeneficiaryError(null);
                      }}
                      onKeyDown={handleEditBeneficiaryKeyDown}
                      className="add-email-input"
                      autoFocus
                    />
                    <div className="beneficiary-edit-actions">
                      <button
                        className="remove-email-button"
                        onClick={handleSaveEditBeneficiary}
                        disabled={isSavingBeneficiary || editingBeneficiaryValue.trim() === originalBeneficiaryValue.trim() || !editingBeneficiaryValue.trim()}
                        title="Save"
                      >
                        {isSavingBeneficiary ? (
                          <PiSpinnerGap className="spin-animation" />
                        ) : (
                          <PiCheck />
                        )}
                      </button>
                      <button
                        className="remove-email-button"
                        onClick={handleCancelEditBeneficiary}
                        disabled={isSavingBeneficiary}
                        title="Cancel"
                      >
                        <PiX />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="email-address">{beneficiary}</span>
                    <div className="beneficiary-edit-actions">
                      {index === 0 ? (
                        <span className="email-badge">Default</span>
                      ) : (
                        <button
                          className="remove-email-button default-beneficiary-button"
                          onClick={() => handleSetDefaultBeneficiary(index)}
                          disabled={settingDefaultIndex !== null || editingBeneficiaryIndex !== null}
                          title="Set as default"
                        >
                          {settingDefaultIndex === index ? (
                            <PiSpinnerGap className="spin-animation" />
                          ) : (
                            <PiStar />
                          )}
                        </button>
                      )}
                      <button
                        className="remove-email-button"
                        onClick={() => handleStartEditBeneficiary(index)}
                        title="Edit beneficiary"
                      >
                        <PiPencil />
                      </button>
                      <button
                        className="remove-email-button"
                        onClick={() => handleRemoveBeneficiary(index)}
                        disabled={deletingBeneficiaryIndex === index || beneficiaries.length <= 1}
                        title={beneficiaries.length <= 1 ? "Cannot delete the last beneficiary" : "Remove beneficiary"}
                      >
                        {deletingBeneficiaryIndex === index ? (
                          <PiSpinnerGap className="spin-animation" />
                        ) : (
                          <PiTrash />
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Add new beneficiary input */}
          <div className="add-email-container">
            <input
              type="text"
              placeholder="Add a beneficiary"
              value={newBeneficiary}
              onChange={(e) => {
                setNewBeneficiary(e.target.value);
                setBeneficiaryError(null);
              }}
              onKeyDown={handleBeneficiaryKeyDown}
              className={`add-email-input ${beneficiaryError ? "input-error" : ""}`}
            />
            <ActionButton
              icon={
                isAddingBeneficiary ? (
                  <PiSpinnerGap className="spin-animation" />
                ) : (
                  <PiPlus />
                )
              }
              text="Add"
              onClick={handleAddBeneficiary}
              secondary={true}
              disabled={isAddingBeneficiary}
              responsive={true}
            />
          </div>
          {beneficiaryError && <p className="email-error-message">{beneficiaryError}</p>}
          {beneficiaryMessage && <p className="beneficiary-success-message">{beneficiaryMessage}</p>}
        </div>

        {/* Support Contact Section */}
        <div className="settings-label">
          <div className="container-label-header">
            <h2>Support</h2>
            <p>Have a request or need help? Contact our support team.</p>
          </div>
          <div className="support-contact">
            <p>support@shoebox.io</p>
          </div>
        </div>

        {/* Subscription Confirmation Popup */}
        {showSubscriptionPopup && (
          <div className="popup-overlay">
            <div className="popup-content">
              <h3>Leaving Shoebox</h3>
              <p>
                You're about to be redirected to Stripe's billing portal to
                manage your subscription.
              </p>
              <div className="popup-actions">
                <ActionButton
                  text="Continue"
                  onClick={confirmSubscriptionNavigation}
                  responsive={false}
                />
                <ActionButton
                  text="Cancel"
                  onClick={cancelSubscriptionNavigation}
                  secondary={true}
                  responsive={false}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
